<template>
	<section
		class="group-card"
		:aria-labelledby="`group-${rule.id || fallbackKey}-title`"
		:data-testid="`grouping-rule-card-${rule.id || fallbackKey}`"
	>
		<header class="group-header">
			<div
				class="order-badge"
				aria-hidden="true"
			>
				{{ orderNumber }}
			</div>
			<div class="group-title-block">
				<h3
					:id="`group-${rule.id || fallbackKey}-title`"
					class="group-title"
				>
					{{ rule.passName }}
				</h3>
				<div class="group-meta">
					{{ rule.passFields.length }} {{ (rule.passFields.length === 1 ? 'field' : 'fields') | useLabels }}
				</div>
			</div>
			<div class="group-controls">
				<v-menu
					v-if="userFullPermissions"
					offset-y
					left
				>
					<template #activator="{ on, attrs }">
						<v-btn
							icon
							small
							v-bind="attrs"
							:aria-label="`Actions for ${rule.passName || 'rule'}`"
							:data-testid="`grouping-rule-${rule.id || fallbackKey}-menu`"
							v-on="on"
							@click.stop
						>
							<v-icon>
								mdi-dots-horizontal
							</v-icon>
						</v-btn>
					</template>
					<v-list dense>
						<v-list-item
							:disabled="isFirst"
							:data-testid="`grouping-rule-${rule.id || fallbackKey}-menu-move-up`"
							@click="$emit('move-up')"
						>
							<v-list-item-title>
								{{ 'Move up' | useLabels }}
							</v-list-item-title>
						</v-list-item>
						<v-list-item
							:disabled="isLast"
							:data-testid="`grouping-rule-${rule.id || fallbackKey}-menu-move-down`"
							@click="$emit('move-down')"
						>
							<v-list-item-title>
								{{ 'Move down' | useLabels }}
							</v-list-item-title>
						</v-list-item>
						<v-list-item
							:data-testid="`grouping-rule-${rule.id || fallbackKey}-menu-edit`"
							@click="$emit('edit')"
						>
							<v-list-item-title>
								{{ 'Edit' | useLabels }}
							</v-list-item-title>
						</v-list-item>
						<v-list-item
							:data-testid="`grouping-rule-${rule.id || fallbackKey}-menu-delete`"
							@click="$emit('delete')"
						>
							<v-list-item-title>
								{{ 'Delete' | useLabels }}
							</v-list-item-title>
						</v-list-item>
					</v-list>
				</v-menu>
			</div>
		</header>
		<div class="group-body">
			<div class="field-label">
				{{ 'Group by' | useLabels }}
			</div>
			<div
				class="field-chips"
				:data-testid="`grouping-rule-${rule.id || fallbackKey}-chips`"
			>
				<span
					v-for="field in rule.passFields"
					:key="field"
					class="chip"
				>
					<span class="chip-dot" />{{ field }}
				</span>
			</div>
		</div>
	</section>
</template>

<script>
export default {
	name: 'grouping-rule-card',
	props: {
		rule: { type: Object, required: true },
		orderNumber: { type: Number, required: true },
		isFirst: { type: Boolean, default: false },
		isLast: { type: Boolean, default: false },
		userFullPermissions: { type: Boolean, default: false },
		index: { type: Number, default: 0 }
	},
	computed: {
		fallbackKey () { return `new-${this.index}-${this._uid}` }
	}
}
</script>

<style scoped>
/* Grouping rules share the primary accent with matching rules — differentiation is conveyed by surrounding content (card shape, chips, screen title), not colour. */
.group-card {
	background: var(--card);
	border: 1px solid var(--border);
	border-radius: var(--radius);
	box-shadow: var(--shadow-sm);
	margin-bottom: var(--spacing-md);
	overflow: hidden;
}
.group-header {
	display: flex;
	align-items: center;
	padding: var(--spacing-md) var(--spacing-lg);
	background: var(--muted);
	border-bottom: 1px solid var(--border);
	gap: var(--spacing-md);
}
.order-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: var(--radius-full);
	background: var(--primary);
	color: var(--primary-foreground);
	font-weight: var(--font-weight-semibold);
	font-size: var(--font-size-sm);
	flex-shrink: 0;
}
.group-title-block {
	flex: 1;
	min-width: 0;
}
.group-title {
	font-size: var(--font-size-base);
	font-weight: var(--font-weight-semibold);
	line-height: var(--line-height-tight);
	margin: 0;
	color: var(--foreground);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.group-meta {
	font-size: var(--font-size-xs);
	color: var(--muted-foreground);
	margin-top: 2px;
}
.group-controls {
	display: flex;
	align-items: center;
	gap: var(--spacing-xs);
	flex-shrink: 0;
}
.group-body {
	padding: var(--spacing-md) var(--spacing-lg);
}
.field-label {
	font-size: var(--font-size-xs);
	text-transform: uppercase;
	color: var(--muted-foreground);
	font-weight: var(--font-weight-semibold);
	letter-spacing: 0.05em;
	margin-bottom: var(--spacing-sm);
}
.field-chips {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}
.chip {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 4px 10px;
	background: var(--muted);
	border-radius: var(--radius-full);
	font-size: var(--font-size-sm);
	color: var(--foreground);
	border: 1px solid var(--border);
}
.chip-dot {
	display: inline-block;
	width: 6px;
	height: 6px;
	background: var(--primary);
	border-radius: var(--radius-full);
}
</style>
